import { ButtonInfo, TicketTypeProps } from './types';
import { FC, useEffect, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import { useHistory, useParams } from 'react-router-dom';

import { PunchupDivider } from 'features/common/components/Punchup/primitives/layout/PunchupDivider';
import { IdParamsType } from 'utils/types';
import { AddTicketTypeCard } from './TicketTypeCard/AddTicketTypeCard';
import { PunchupCardTileGrid } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupInlineEditor } from 'features/common/components/Punchup/composites/editors/PunchupInlineEditor';
import { PunchupMultiButtonSectionTitle } from 'features/common/components/Punchup/primitives/layout/PunchupMultiButtonSectionTitle';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { TicketTypeGrid } from './TicketTypeGrid/TicketTypeGrid';
import { ROUTES } from 'features/routes';
import { setTicketsAvailable } from 'store/slices/BoxOfficeSlice';
import { sortTicketTypes } from './utils';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { useTicketTypesApi } from './useTicketTypes';
import { useTranslation } from 'react-i18next';
import { TicketTypeForm } from './TicketTypeForm/TicketTypeForm';

interface TicketTypesSectionProps {
	/**
	 * When true the section is rendered inside a PunchupAccordionSection.
	 * Add/Edit are handled inline (no sub-page navigation).
	 */
	inAccordion?: boolean;
}

const TicketTypesSection: FC<TicketTypesSectionProps> = ({ inAccordion = false }) => {
	const { t } = useTranslation();
	const { push } = useHistory();
	const dispatch = useAppDispatch();
	const { id } = useParams<IdParamsType>();
	const isLargeScreen = useResponsiveScreen();
	const { ticketTypes: ticketTypesData, isFetching: isTicketTypesFetching } = useTicketTypesApi();
	const { event } = useGetEvent();
	const isSeated = !!event?.seatsEventKey;

	const [ticketTypes, setTicketTypes] = useState<TicketTypeProps[]>([]);

	useEffect(() => {
		if (ticketTypesData) {
			setTicketTypes(sortTicketTypes(ticketTypesData));
			dispatch(setTicketsAvailable(ticketTypesData.length > 0));
		}
	}, [ticketTypesData]);

	const onClickAddNew = () => push(ROUTES.TICKET_NEW_PATH.replace(':id', id));

	const buttons: ButtonInfo[] = [
		{
			label: t('dashboard-add-green'),
			onClick: onClickAddNew,
			dataCy: 'add-ticket',
			isAdd: true,
		},
	];

	// ── Accordion (inline add/edit) mode ────────────────────────────

	if (inAccordion) {
		return (
			<PunchupInlineEditor
				addLabel={t('dashboard-add-green')}
				extraViewActions={isTicketTypesFetching ? <PunchupSpinner size={16} /> : undefined}
				addDisabled={isSeated}
				addDisabledTitle={
					isSeated
						? t(
								'dashboard-tickets-add-disabled-seated',
								'Ticket types for this show are derived from seats.io categories. To add a tier, add a category in the seats chart above.'
						  )
						: undefined
				}
				renderView={({ startEdit }) =>
					ticketTypes?.length > 0 ? (
						<TicketTypeGrid ticketTypes={ticketTypes} onEdit={(ticketId) => startEdit(String(ticketId))} />
					) : (
						<p className="punchupEmptyState">{t('dashboard-no-ticket-types', 'No ticket types yet')}</p>
					)
				}
				renderEdit={({ editingId, done }) => <TicketTypeForm ticketId={editingId} onDone={done} />}
			/>
		);
	}

	// ── Standalone (non-accordion) mode ─────────────────────────────

	return (
		<>
			<PunchupMultiButtonSectionTitle
				title={t('dashboard-tickets') + (ticketTypes?.length > 0 ? ` (${ticketTypes.length})` : '')}
				buttons={buttons}
				inAccordion={false}
			/>

			<PunchupCardTileGrid data-cy="tickets-section">
				{ticketTypes?.length > 0 ? (
					<TicketTypeGrid ticketTypes={ticketTypes} />
				) : (
					<AddTicketTypeCard onClick={onClickAddNew} />
				)}
			</PunchupCardTileGrid>

			{!isLargeScreen && <PunchupDivider />}
		</>
	);
};

export { TicketTypesSection };
